import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Slider,
  TextField,
  Card,
  CardMedia,
  CardContent,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Palette as PaletteIcon,
  ErrorOutline as InfoExclamationIcon,
  Cancel as LargeRedXIcon,
} from '@mui/icons-material';
import {
  validateMasterLogo,
  generateBrandingIcons,
  previewBrandingIcon,
  BrandingIconType,
  LogoGenerationOptions,
  LogoValidationResult,
} from '../services/api';

interface LogoWizardProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  onSuccess?: () => void;
}

const steps = ['Upload Master Logo', 'Preview & Adjust', 'Generate Icons', 'Complete'];

const iconTypeLabels: Record<BrandingIconType, string> = {
  [BrandingIconType.BannerLogo]: 'Banner Logo',
  [BrandingIconType.SquareLogoDark]: 'Square Logo (Dark)',
  [BrandingIconType.SquareLogo]: 'Square Logo',
  [BrandingIconType.Favicon]: 'Favicon',
  [BrandingIconType.HeaderLogo]: 'Header Logo',
  [BrandingIconType.BackgroundImage]: 'Background Image',
};

const iconTypeDimensions: Record<BrandingIconType, string> = {
  [BrandingIconType.BannerLogo]: '245×36px',
  [BrandingIconType.SquareLogoDark]: '240×240px',
  [BrandingIconType.SquareLogo]: '240×240px',
  [BrandingIconType.Favicon]: '32×32px',
  [BrandingIconType.HeaderLogo]: '245×36px',
  [BrandingIconType.BackgroundImage]: '1920×1080px',
};

const LogoWizard: React.FC<LogoWizardProps> = ({ open, onClose, tenantId, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<LogoValidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  
  // Generation options
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [padding, setPadding] = useState(10);
  const [backgroundOpacity, setBackgroundOpacity] = useState(30);
  
  // Preview images
  const [previewImages, setPreviewImages] = useState<Record<BrandingIconType, string>>({} as Record<BrandingIconType, string>);
  const [previewWarnings, setPreviewWarnings] = useState<Partial<Record<BrandingIconType, string>>>({});
  const [generationFailures, setGenerationFailures] = useState<Partial<Record<BrandingIconType, string>>>({});
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setFilePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const input = fileInputRef.current;
      if (input) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        input.files = dataTransfer.files;
        handleFileSelect({ target: input } as any);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const validateLogo = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);

    try {
      console.log('Validating logo:', selectedFile.name, selectedFile.type, selectedFile.size);
      const result = await validateMasterLogo(tenantId, selectedFile);
      console.log('Validation result:', result);
      setValidationResult(result);
      
      if (result.isValid) {
        setActiveStep(1);
        // Generate initial previews
        await generatePreviews();
      } else {
        setError(result.errorMessage || 'Logo validation failed');
      }
    } catch (err: any) {
      console.error('Validation error:', err);
      console.error('Error response:', err.response);
      const errorMsg = err.response?.data?.error || err.response?.data?.errorMessage || err.message || 'Failed to validate logo';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const generatePreviews = async () => {
    if (!selectedFile) return;

    setLoading(true);
    const options: LogoGenerationOptions = {
      backgroundColor,
      padding,
      backgroundOpacity,
    };

    try {
      setGenerationFailures({});
      // Generate previews for all 6 icons
      const iconTypes = [
        BrandingIconType.BannerLogo,
        BrandingIconType.SquareLogoDark,
        BrandingIconType.SquareLogo,
        BrandingIconType.Favicon,
        BrandingIconType.HeaderLogo,
        BrandingIconType.BackgroundImage,
      ];

      const previews: Record<BrandingIconType, string> = {} as Record<BrandingIconType, string>;
      const warnings: Partial<Record<BrandingIconType, string>> = {};

      for (const iconType of iconTypes) {
        const previewResult = await previewBrandingIcon(tenantId, selectedFile, iconType, options);
        const url = URL.createObjectURL(previewResult.blob);
        previews[iconType] = url;

        if (previewResult.warningMessage) {
          warnings[iconType] = previewResult.warningMessage;
        }
      }

      setPreviewImages(previews);
      setPreviewWarnings(warnings);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate previews');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);
    setActiveStep(2);
    setGenerationProgress(0);

    const options: LogoGenerationOptions = {
      backgroundColor,
      padding,
      backgroundOpacity,
    };

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setGenerationProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const result = await generateBrandingIcons(tenantId, selectedFile, options);

      const failures: Partial<Record<BrandingIconType, string>> = {};
      if (result.iconResults) {
        for (const iconResult of result.iconResults) {
          if (!iconResult.success) {
            const iconType = iconResult.iconType as BrandingIconType;
            failures[iconType] =
              iconResult.errorMessage ||
              'This image could not meet the minimum required size and must be replaced.';
          }
        }
      }

      setGenerationFailures(failures);

      clearInterval(progressInterval);
      setGenerationProgress(100);

      if (result.success) {
        if (result.hasFailures) {
          setError(
            'Generation completed with issues. Replace images marked with a red X and regenerate those assets.'
          );
          setActiveStep(1);
        } else {
          setTimeout(() => {
            setActiveStep(3);
          }, 500);
        }
      } else {
        setError(result.error || 'Failed to generate icons');
        setActiveStep(1);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate branding icons');
      setActiveStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Clean up preview URLs
    Object.values(previewImages).forEach((url) => URL.revokeObjectURL(url));
    
    setActiveStep(0);
    setSelectedFile(null);
    setFilePreview(null);
    setValidationResult(null);
    setError(null);
    setPreviewImages({} as Record<BrandingIconType, string>);
    setPreviewWarnings({});
    setGenerationFailures({});
    setGenerationProgress(0);
    onClose();
  };

  const handleComplete = () => {
    handleClose();
    onSuccess?.();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Upload
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <Paper
              elevation={0}
              sx={{
                p: 4,
                border: '2px dashed',
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Drop your master logo here or click to browse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Minimum 300×300px • PNG or JPEG • Max 10MB
              </Typography>
            </Paper>

            {filePreview && (
              <Box sx={{ mt: 3 }}>
                <Paper elevation={2} sx={{ p: 2, display: 'inline-block' }}>
                  <img
                    src={filePreview}
                    alt="Master logo preview"
                    style={{
                      maxWidth: '300px',
                      maxHeight: '300px',
                      display: 'block',
                    }}
                  />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {selectedFile?.name}
                  </Typography>
                  {validationResult && (
                    <Typography variant="caption" color="text.secondary">
                      {validationResult.width}×{validationResult.height} • {validationResult.format}
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}
          </Box>
        );

      case 1: // Preview & Adjust
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Adjust Generation Settings
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>Background Color</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    placeholder="#FFFFFF"
                  />
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    style={{ width: 60, height: 40, cursor: 'pointer' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Also used to render stylized fill bands when your logo&apos;s aspect ratio differs from the target slot.
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>Padding: {padding}px</Typography>
                <Slider
                  value={padding}
                  onChange={(_, value) => setPadding(value as number)}
                  min={0}
                  max={30}
                  valueLabelDisplay="auto"
                />
                <Typography variant="caption" color="text.secondary">
                  Higher padding can trigger stylized fill bands on Banner and Header logos.
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>Background Opacity: {backgroundOpacity}%</Typography>
                <Slider
                  value={backgroundOpacity}
                  onChange={(_, value) => setBackgroundOpacity(value as number)}
                  min={0}
                  max={100}
                  valueLabelDisplay="auto"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={generatePreviews}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                >
                  Update Previews
                </Button>
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Preview
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(previewImages).map(([iconType, url]) => (
                <Grid item xs={12} sm={6} md={4} key={iconType}>
                  <Card>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        image={url}
                        alt={iconTypeLabels[iconType as BrandingIconType]}
                        sx={{
                          height: 150,
                          objectFit: 'contain',
                          backgroundColor: 'action.hover',
                          p: 2,
                        }}
                      />
                      {generationFailures[iconType as BrandingIconType] && (
                        <Tooltip
                          title={
                            generationFailures[iconType as BrandingIconType] ||
                            'This image could not meet the minimum required size and must be replaced.'
                          }
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(255,255,255,0.95)',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <LargeRedXIcon sx={{ fontSize: 42, color: 'error.main' }} />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {iconTypeLabels[iconType as BrandingIconType]}
                        </Typography>
                        {previewWarnings[iconType as BrandingIconType] && (
                          <Tooltip title={previewWarnings[iconType as BrandingIconType] || ''}>
                            <InfoExclamationIcon sx={{ fontSize: 16, color: 'info.main' }} />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {iconTypeDimensions[iconType as BrandingIconType]}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case 2: // Generating
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AutoAwesomeIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Generating Branding Icons...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Creating all 6 branding icons from your master logo
            </Typography>
            <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
              <LinearProgress variant="determinate" value={generationProgress} />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                {generationProgress}%
              </Typography>
            </Box>
          </Box>
        );

      case 3: // Complete
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Icons Generated Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              All 6 branding icons have been generated and uploaded. You can now view them in the Assets tab
              or deploy them to your Microsoft 365 tenant.
            </Typography>
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Generated Icons:</strong>
              </Typography>
              <ul style={{ textAlign: 'left', margin: '8px 0 0 0' }}>
                {Object.entries(iconTypeLabels).map(([key, label]) => (
                  <li key={key}>{label}</li>
                ))}
              </ul>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon />
          <span>Logo Wizard</span>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading && activeStep === 2}>
          {activeStep === 3 ? 'Close' : 'Cancel'}
        </Button>
        
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={validateLogo}
            disabled={!selectedFile || loading}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? 'Validating...' : 'Next'}
          </Button>
        )}
        
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading}
              startIcon={<AutoAwesomeIcon />}
            >
              Generate All Icons
            </Button>
          </>
        )}
        
        {activeStep === 3 && (
          <Button variant="contained" onClick={handleComplete} color="success">
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LogoWizard;
